import type { LoadedDocument, PageText } from '@asvise/read'
import type { Citation } from '@asvise/facts'
import type { CoverageCode } from '@asvise/household-engine/vocabulary'

export type { LoadedDocument, PageText }

/**
 * Reading a life or health policy out of the PDF an insurer sent.
 *
 * Two families side by side, and the split is the whole design.
 *
 * `Raw*` is what a model said before anything believed it. The browser carries
 * these between passes and never knows what makes any of them acceptable.
 *
 * `*Reading` is what survived being looked for in the document, each carrying
 * the span it was found in. Only these may reach a person.
 *
 * Benefit-shaped rather than section-shaped, which is the difference from the
 * commercial reader. A commercial schedule grants sections of cover with limits
 * and sub-limits; a life schedule grants benefits to a named person, some of
 * which are three claims against one sum. What has to be modelled here and does
 * not exist there is the pool, the acceleration, and who each benefit insures.
 */

/** What the model answers with: a value, and the line it read it from. */
export interface RawField {
  value: string
  verbatim: string
}

/** A figure, where the figure is the point. */
export interface RawAmount {
  value: number
  verbatim: string
}

/**
 * How a benefit relates to the others on its policy, as the schedule states it.
 *
 * The single most consequential field in this reader. A schedule printing
 * S$100,000, S$100,000 and S$50,000 against death, disability and critical
 * illness is stating one pool of S$100,000, and a reading that calls all three
 * ADDITIONAL reports S$250,000 of protection that does not exist.
 */
export interface RawBasis {
  value: 'ACCELERATED' | 'ADDITIONAL'
  verbatim: string
}

/** Waiting and survival periods, in days, as the schedule states them. */
export interface RawPeriod {
  value: number
  verbatim: string
}

/** What a household pays towards a reimbursed bill. */
export interface RawCostSharing {
  deductible?: RawAmount
  /** Stated as a percentage on the page, carried as a fraction of one. */
  co_insurance?: { value: number; verbatim: string }
  annual_limit?: RawAmount
  out_of_pocket_cap?: RawAmount
  /** Whether a rider buys either of them down. A schedule that says "none" is answering. */
  rider?: RawField
}

/** One benefit, as the model reports it. */
export interface RawBenefit {
  code: CoverageCode
  /** The row it was found on, quoted. */
  verbatim: string
  /**
   * Whether the policy grants it.
   *
   * False is a definite answer: a schedule listing a benefit and showing it as
   * not included has told the reader something. Absent is not the same, and the
   * two are kept apart for the reason the commercial reader keeps them apart:
   * not listed is not the same as not insured.
   */
  operative: boolean
  amount?: RawAmount
  basis?: RawBasis
  /**
   * The pool this draws on, where the schedule states one.
   *
   * A figure, not a name: what the page says is "total guaranteed payments from
   * that pool cannot exceed S$100,000", and the number is what makes the
   * benefits underneath it unaddable.
   */
  pool_total?: RawAmount
  waiting_days?: RawPeriod
  survival_days?: RawPeriod
  expires_at_age?: RawPeriod
  expires_on?: RawField
  cost_sharing?: RawCostSharing
  /** Who the schedule says this insures, in the insurer's own words. */
  insured_as_printed?: RawField
}

/** The whole reply to a pass. */
export interface RawPolicy {
  insurer?: RawField
  product_name?: RawField
  policy_number?: RawField
  policyholder?: RawField
  insured?: RawField
  beneficiary?: RawField
  start_date?: RawField
  /** Absent or "whole life" where the cover has no end. Not the premium term. */
  end_date?: RawField
  /** When the premiums stop, which is not when the cover stops. */
  premium_term_end?: RawField
  premium_amount?: RawAmount
  premium_frequency?: RawField
  /** Whether the rate is guaranteed for the term. A reviewable rate is a finding. */
  premium_guaranteed?: RawField
  benefits: RawBenefit[]
}

/** A value that was found in the document, with where it was found. */
export interface Reading<T> {
  value: T
  citation: Citation
}

export interface BenefitReading {
  code: CoverageCode
  operative: boolean
  found: Citation
  amount: Reading<number> | null
  basis: Reading<'ACCELERATED' | 'ADDITIONAL'> | null
  pool_total: Reading<number> | null
  waiting_days: Reading<number> | null
  survival_days: Reading<number> | null
  expires_at_age: Reading<number> | null
  expires_on: Reading<string> | null
  deductible: Reading<number> | null
  co_insurance: Reading<number> | null
  annual_limit: Reading<number> | null
  out_of_pocket_cap: Reading<number> | null
  cost_sharing_rider: Reading<boolean> | null
  insured_as_printed: Reading<string> | null
}

/**
 * Which reader produced a policy, stamped on every one it reads.
 *
 * Framework section 10.3 wants it on the audit record, and the reason is not
 * bookkeeping: a reading is a claim about what a document says, and the thing
 * that makes a wrong claim diagnosable months later is knowing which version
 * of the passes and the validators made it. It moves when a pass is reworded
 * or a validator changes what it will accept.
 */
export const READER_VERSION = '2026.10.01'

export const HEADER_FIELDS = [
  'insurer',
  'product_name',
  'policy_number',
  'policyholder',
  'insured',
  'beneficiary',
  'start_date',
  'end_date',
  'premium_term_end',
  'premium_amount',
  'premium_frequency',
  'premium_guaranteed',
] as const
export type HeaderField = (typeof HEADER_FIELDS)[number]

export const HEADER_LABEL: Record<HeaderField, string> = {
  insurer: 'Insurer',
  product_name: 'Plan',
  policy_number: 'Policy number',
  policyholder: 'Policyholder',
  insured: 'Who it insures',
  beneficiary: 'Who would be paid',
  start_date: 'Cover started',
  end_date: 'Cover ends',
  premium_term_end: 'Premiums stop',
  premium_amount: 'Premium',
  premium_frequency: 'How often',
  premium_guaranteed: 'Rate guaranteed',
}

export interface DocumentReading {
  document: string
  insurer: Reading<string> | null
  product_name: Reading<string> | null
  policy_number: Reading<string> | null
  policyholder: Reading<string> | null
  insured: Reading<string> | null
  /** Null where the schedule records none, which is itself worth telling somebody. */
  beneficiary: Reading<string> | null
  start_date: Reading<string> | null
  end_date: Reading<string> | null
  premium_term_end: Reading<string> | null
  premium_amount: Reading<number> | null
  premium_frequency: Reading<string> | null
  premium_guaranteed: Reading<boolean> | null
  benefits: BenefitReading[]
}

export function emptyReading(document = ''): DocumentReading {
  return {
    document,
    insurer: null,
    product_name: null,
    policy_number: null,
    policyholder: null,
    insured: null,
    beneficiary: null,
    start_date: null,
    end_date: null,
    premium_term_end: null,
    premium_amount: null,
    premium_frequency: null,
    premium_guaranteed: null,
    benefits: [],
  }
}

/**
 * The document-quality gate's vocabulary, which the interface renders.
 *
 * Here rather than beside the checks, and the split is the same one this
 * package draws everywhere else. A screen has to print the name of a check and
 * the verdict against it; what makes a document fail one is judgement about
 * documents, and `exports` refuses a browser file the path that holds it. Put
 * in one file, importing a label would have pulled every check into the
 * bundle, and `npm run check:bundle -w apps/household` is what says so.
 */
export const GATE_CHECKS = [
  'NAMES_INSURER',
  'STATES_POLICY_NUMBER',
  'IDENTIFIES_PEOPLE',
  'STATES_A_TERM',
  'PAGES_COMPLETE',
  'ONE_POLICY',
  'VERSION_CURRENT',
] as const
export type GateCheck = (typeof GATE_CHECKS)[number]

export const GATE_LABEL: Record<GateCheck, string> = {
  NAMES_INSURER: 'The insurer is named',
  STATES_POLICY_NUMBER: 'A policy number is printed',
  IDENTIFIES_PEOPLE: 'Who it insures and who owns it can be identified',
  STATES_A_TERM: 'A start, an end or a term is visible',
  PAGES_COMPLETE: 'The pages are complete enough to read the schedule',
  ONE_POLICY: 'One policy, not several combined',
  VERSION_CURRENT: 'This is the current version',
}

/**
 * PASS, REVIEW, or nothing established either way.
 *
 * REVIEW is the only one that marks the document. See the header: a check that
 * could not be run is not a check that failed, and conflating them puts a flag
 * on every file.
 */
export type GateVerdict = 'PASS' | 'REVIEW' | 'UNRESOLVED'

export interface GateFinding {
  check: GateCheck
  verdict: GateVerdict
  /** What the document showed, or why nothing could be established. */
  said: string
  /** The line this was decided from, where there was one. */
  citation?: Citation
}

export interface Gate {
  /** What the framework calls this document's state. */
  verdict: 'READY' | 'DOCUMENT_REVIEW_REQUIRED'
  findings: GateFinding[]
  /** Only the reasons this document is marked, for carrying onto the policy. */
  review_reasons: string[]
}

/**
 * Something the model said that the document did not support.
 *
 * Kept rather than counted, because a paraphrasing model and a schedule that
 * genuinely lacks a benefit produce the same silence otherwise, and only the
 * first of those is a reason to distrust the whole reading.
 */
export interface Dropped {
  what: string
  quoted: string
  why:
    | 'the quoted line is not in the document'
    | 'the value is not in the line it was quoted from'
    | 'the date in the line is not the date that was given'
    | 'the line states more than one figure'
    | 'the line states no figure'
    | 'the line does not say the benefit is accelerated'
    | 'the line states more than one share'
    | 'the benefit is not one this screening reads'
}

export interface ReadOutcome {
  summary: string
  reading: DocumentReading
  discarded: Dropped[]
  /**
   * The exact text that was searched, page by page.
   *
   * What separates "the model invented a row" from "the page was rebuilt badly
   * and the row is really there". Without it those two look identical and the
   * first person to meet the second one blames the model.
   */
  read: PageText[]
}

/** A file with no page that reads as a policy schedule. Find another file. */
export class NoSchedule extends Error {
  override name = 'NoSchedule'
}
